import React from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import {
  Boxes, FileText, Globe, Image, LayoutDashboard, ListTree, LogOut,
  MessageSquare, Package, ShoppingBag, Sparkles, Star, Settings as Cog, Wrench, Scale,
  Send, Users, Flag,
} from "lucide-react";
import { useAdminAuth } from "./AdminAuth";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/catalogue", label: "Catalogue items", icon: Boxes },
  { to: "/admin/stock", label: "Stock & Google feed", icon: Package },
  { to: "/admin/weights", label: "Shipping weights", icon: Scale },
  { to: "/admin/polish", label: "Content polish", icon: Sparkles },
  { to: "/admin/products", label: "Shop products (legacy)", icon: Package },
  { to: "/admin/media", label: "Media library", icon: Image },
  { to: "/admin/builds", label: "Our builds", icon: Wrench },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/nav", label: "Navigation", icon: ListTree },
  { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare },
  { to: "/admin/contacts", label: "Contacts", icon: Users },
  { to: "/admin/mailouts", label: "Mailouts", icon: Send },
  { to: "/admin/racex", label: "RACEX event", icon: Flag },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/settings", label: "Settings", icon: Cog },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();

  return (
    <div className="flex min-h-screen" style={{ background: "#0E1210" }}>
      <aside
        className="scrollbar-thin sticky top-0 hidden h-screen w-60 shrink-0 overflow-y-auto border-r lg:block"
        style={{ background: "var(--nq-ink)", borderColor: "rgba(255,255,255,0.08)" }}
      >
        <div className="border-b px-5 py-4" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <Link to="/admin" className="text-lg font-bold">
            NQ<span className="gold-text">SPRINGS</span>
          </Link>
          <p className="mt-0.5 text-[10px] uppercase tracking-widest text-[color:var(--nq-stone)]">
            Content manager
          </p>
        </div>

        <nav className="space-y-0.5 p-3" data-testid="admin-sidebar">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition ${
                  isActive
                    ? "font-semibold text-[color:var(--nq-black)]"
                    : "text-[color:var(--nq-stone)] hover:bg-white/5 hover:text-[color:var(--nq-gold)]"
                }`
              }
              style={({ isActive }) => (isActive ? { background: "var(--nq-gold)" } : {})}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <l.icon className="h-4 w-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-4 border-t p-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-[color:var(--nq-stone)] hover:bg-white/5"
          >
            <Globe className="h-4 w-4" /> View site
          </Link>
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-[color:var(--nq-stone)] hover:bg-white/5"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
          <p className="mt-2 truncate px-3 text-[10px] text-[color:var(--nq-stone)]">{user?.email}</p>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header
          className="scrollbar-thin flex items-center gap-2 overflow-x-auto border-b px-4 py-2 lg:hidden"
          style={{ background: "var(--nq-ink)", borderColor: "rgba(255,255,255,0.08)" }}
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs ${
                  isActive ? "font-semibold text-[color:var(--nq-black)]" : "text-[color:var(--nq-stone)]"
                }`
              }
              style={({ isActive }) => (isActive ? { background: "var(--nq-gold)" } : {})}
              data-testid={`admin-mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </header>

        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
